import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Catálogo de los componentes de `pages/shared/`.
 *
 * Esta carpeta no pertenece a ninguna fase: la usan pantallas de tres módulos
 * distintos —`oneUser` (1.11), `organizations` (1.11.d) y `policytoolsSettings`
 * (1.12)— y `AgentInfoTooltip` además vive dentro de cuatro carpetas ya
 * cerradas (`contacts`, `policies`, `quotes`, `signatureFormsEntries`). Por eso
 * se migró aparte, en el slice 1.11.c.2.c.
 *
 * Namespaces: `admin.email.*` para los dos formularios de correo, que solo
 * salen en pantallas de administración; `common.*` para el tooltip de agente,
 * que es genuinamente transversal.
 *
 * **No se traducen** los nombres de variables de entorno (`EMAIL_HOST`,
 * `DEFAULT_FROM_EMAIL`, `PUBLIC_REQUIRE_IDENTITY`…): son identificadores de
 * configuración, no texto de interfaz. Viven en `ENV` más abajo. Lo mismo con
 * los protocolos y marcas (`SMTP`, `Gmail`, `AWS SES`) y con los valores de
 * ejemplo (`smtp.gmail.com`, los puertos).
 */

/** Nombres de variables de entorno. Identificadores, nunca texto traducible. */
export const ENV = {
  host: 'EMAIL_HOST',
  port: 'EMAIL_PORT',
  useTls: 'EMAIL_USE_TLS',
  hostUser: 'EMAIL_HOST_USER',
  hostPassword: 'EMAIL_HOST_PASSWORD',
  defaultFrom: 'DEFAULT_FROM_EMAIL',
  publicRequireIdentity: 'PUBLIC_REQUIRE_IDENTITY',
} as const

/** Modos de entrega: nombres de protocolo y de producto. */
export const DELIVERY_MODE = {smtp: 'SMTP', gmail: 'Gmail'} as const

/** Valores de ejemplo y puertos: datos, no texto. */
export const SMTP_EXAMPLE_HOST = 'smtp.gmail.com'
export const PORT_TLS = '587'
export const PORT_PLAIN = '25'
/** Máscara del campo de contraseña. */
export const PASSWORD_MASK = '••••••••'
/** Separador entre el nombre de la variable y su descripción. */
export const EM_DASH = ' — '

export const EMAIL_MESSAGES = defineMessages({
  // ── Herencia (los dos formularios) ──
  override: {id: 'admin.email.override', defaultMessage: 'Override'},
  inheritsFromUpper: {
    id: 'admin.email.inheritsFromUpper',
    defaultMessage: 'Inherits from the upper level',
  },
  inheritedFromUpper: {
    id: 'admin.email.inheritedFromUpper',
    defaultMessage: 'Inherited from upper level',
  },
  inheritedFromSource: {
    id: 'admin.email.inheritedFromSource',
    defaultMessage: 'Inherited from {source}: {value}',
  },
  seeSignature: {id: 'admin.email.seeSignature', defaultMessage: '(see signature)'},
  sourceSystem: {id: 'admin.email.source.system', defaultMessage: 'System'},
  sourceOrganization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  sourceUser: {id: 'common.field.user', defaultMessage: 'User'},

  // ── Remitente y firma ──
  senderName: {id: 'admin.email.field.senderName', defaultMessage: 'Sender name'},
  senderEmail: {id: 'admin.email.field.senderEmail', defaultMessage: 'Sender email'},
  signature: {id: 'admin.email.field.signature', defaultMessage: 'Signature (Email Signature)'},
  noSignatureUpper: {
    id: 'admin.email.noSignatureUpper',
    defaultMessage: 'No signature configured at any upper level.',
  },
  signaturePreview: {id: 'admin.email.signaturePreview', defaultMessage: 'Signature preview'},
  noSignatureYet: {
    id: 'admin.email.noSignatureYet',
    defaultMessage: 'No signature to preview yet.',
  },

  // ── Envío de prueba (los dos formularios) ──
  testEmailPlaceholder: {
    id: 'admin.email.testPlaceholder',
    defaultMessage: 'Send test email to...',
  },
  sendTestEmail: {id: 'admin.email.sendTest', defaultMessage: 'Send test email'},

  // ── Transporte SMTP ──
  inheritanceHint: {
    id: 'admin.email.inheritanceHint',
    defaultMessage: 'Fields below inherit the system default configuration unless overridden.',
  },
  errDefaultFromRequired: {
    id: 'admin.email.error.defaultFromRequired',
    defaultMessage: '{name} is required when overriding it.',
  },
  errAllSmtpRequired: {
    id: 'admin.email.error.allSmtpRequired',
    defaultMessage: 'All SMTP transport fields are required when overriding this configuration.',
  },
  deliveryMode: {id: 'admin.email.field.deliveryMode', defaultMessage: 'Delivery Mode'},
  smtpServer: {id: 'admin.email.section.smtpServer', defaultMessage: 'SMTP Server'},
  /** `Inherited: host = smtp.gmail.com` cuando el nivel superior sí tiene valor. */
  inheritedPair: {
    id: 'admin.email.inheritedPair',
    defaultMessage: 'Inherited: {label} = {value}',
  },
  hintMode: {id: 'admin.email.hint.mode', defaultMessage: 'mode'},
  hintHost: {id: 'admin.email.hint.host', defaultMessage: 'host'},
  hintPort: {id: 'admin.email.hint.port', defaultMessage: 'port'},
  hintUser: {id: 'admin.email.hint.user', defaultMessage: 'user'},
  hintDefaultFrom: {id: 'admin.email.hint.defaultFrom', defaultMessage: 'default from'},
  /** Placeholder de ejemplo: el valor va aparte porque no se traduce. */
  egValue: {id: 'admin.email.placeholder.eg', defaultMessage: 'e.g. {example}'},
  /**
   * Frase completa por variante en vez de componer: en español el puerto va
   * detrás de la preposición y armarlo por trozos no concuerda (guía §17).
   */
  useTls: {id: 'admin.email.useTls', defaultMessage: 'Use TLS (Port {port})'},
  noTls: {id: 'admin.email.noTls', defaultMessage: 'No TLS (Port {port})'},
  passwordConfigured: {
    id: 'admin.email.passwordConfigured',
    defaultMessage: '(already configured — leave empty to keep unchanged)',
  },
  inheritedConfigured: {
    id: 'admin.email.inheritedConfigured',
    defaultMessage: 'Inherited from upper level (configured)',
  },
  inheritedNotConfigured: {
    id: 'admin.email.inheritedNotConfigured',
    defaultMessage: 'Inherited from upper level (not configured)',
  },
  defaultFromHint: {
    id: 'admin.email.defaultFromHint',
    defaultMessage:
      'Useful when sending through the same SMTP user (e.g. AWS SES) but with a different verified "from" address.',
  },
  publicRequireIdentity: {
    id: 'admin.email.publicRequireIdentity',
    defaultMessage: 'Require identity verification on public forms',
  },
  publicRequireIdentityHint: {
    id: 'admin.email.publicRequireIdentityHint',
    defaultMessage:
      'Global default for public forms. A form token can still override this value individually.',
  },

  // ── Reusadas del catálogo común ──
  save: {id: 'common.action.save', defaultMessage: 'Save'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  sending: {id: 'common.state.sending', defaultMessage: 'Sending...'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  selectOption: {id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'},
})

/**
 * Tooltip de agente. Va en `common.*` porque lo pintan cuatro carpetas ya
 * cerradas de módulos distintos.
 */
export const AGENT_TOOLTIP_MESSAGES = defineMessages({
  title: {id: 'common.agentTooltip.title', defaultMessage: 'Agent information:'},
  noAgent: {id: 'common.agentTooltip.noAgent', defaultMessage: 'No agent assigned'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
})

/**
 * Etiquetas de `TABS_FORM` (`pages/shared/const.ts`), el asistente por pasos que
 * comparten los modales de añadir póliza, cotización y contacto.
 *
 * Se pintan como `{tb.name}` / `{tb.description}`, variables que el rule no ve,
 * y salían en inglés en `quotes/ModalAddQuote` y en las descripciones de
 * `contacts/ModalAddEditContact` — dos carpetas ya cerradas.
 *
 * `policies/ModalAddPolice` **no** usa esto: tiene su propia copia
 * (`policies.list.addPolicy.step.*`), deliberadamente distinta para el asistente
 * de pólizas («Policyholder location» en vez de «Address information»). Va en
 * `common.*` porque lo pintan tres módulos.
 */
const FORM_TAB = defineMessages({
  'policy-holder.name': {id: 'common.formTab.policyHolder.name', defaultMessage: 'Policy Holder'},
  'policy-holder.desc': {
    id: 'common.formTab.policyHolder.desc',
    defaultMessage: 'Contact Information',
  },
  'address-info.name': {id: 'common.formTab.address.name', defaultMessage: 'Address information'},
  // El paso repite el mismo texto como nombre y como descripcion: una sola llave.
  'address-info.desc': {id: 'common.formTab.address.name', defaultMessage: 'Address information'},
  'household-info.name': {
    id: 'common.formTab.household.name',
    defaultMessage: 'Household information',
  },
  'household-info.desc': {
    id: 'common.formTab.household.name',
    defaultMessage: 'Household information',
  },
  'family-members.name': {id: 'common.formTab.family.name', defaultMessage: 'Family Members'},
  'family-members.desc': {
    id: 'common.formTab.family.desc',
    defaultMessage: 'Applicants & dependents',
  },
  'basic-policy-info.name': {
    id: 'common.formTab.basic.name',
    defaultMessage: 'Basic quote information',
  },
  'basic-policy-info.desc': {id: 'common.formTab.basic.desc', defaultMessage: 'Complete'},
  'request-consent.name': {id: 'common.formTab.consent.name', defaultMessage: 'Request Consent'},
  'request-consent.desc': {id: 'common.formTab.consent.name', defaultMessage: 'Request Consent'},
}) as Record<string, MessageDescriptor>

/**
 * Nombre y descripción traducidos de un paso. Los valores desconocidos caen al
 * texto que trae el const, para que un paso nuevo se siga pintando.
 */
export const formTabLabels = (
  intl: IntlShape,
  tab: {tabName: string; name: string; description: string}
) => ({
  name: FORM_TAB[`${tab.tabName}.name`]
    ? intl.formatMessage(FORM_TAB[`${tab.tabName}.name`])
    : tab.name,
  description: FORM_TAB[`${tab.tabName}.desc`]
    ? intl.formatMessage(FORM_TAB[`${tab.tabName}.desc`])
    : tab.description,
})
